/**
 * The Finance → Fees per-cycle payments grid's filter + sort declaration — a
 * consumer of the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `claimsFilterConfig`.
 *
 * IMPORTANT: the SELECTED CYCLE is NOT a facet. The page keeps the
 * `CycleSidebar` / `CycleSelect` picker (local `selectedCycleId` state) as the
 * primary selector and merges `{ cycleId: selectedCycleId, ...apiParams }` into
 * the payments query. This config only declares the facets that filter WITHIN
 * the chosen cycle.
 *
 * Facets exposed:
 *   - search  → `q`                       free text over student name / admission
 *                                          number (the page's pre-existing search
 *                                          contract).
 *   - status  → `status`                  multi-select over pending/paid/overdue/
 *                                          waived/partial. The page's prior
 *                                          default was the "All" chip and the
 *                                          backend treats an *omitted* status as
 *                                          "all" — so the default value is `[]`
 *                                          and "All" is encoded by OMITTING the
 *                                          param (`allEncoding: 'omit'`). This is
 *                                          the load-bearing difference from
 *                                          students/claims, whose backend default
 *                                          is narrower than "all".
 *   - method  → `method`                  multi-select over the 6 payment methods
 *                                          (cash/bank_transfer/upi_offline/cheque/
 *                                          other/razorpay); allEncoding 'omit'.
 *   - due     → `dueFrom`/`dueTo`          date range over the payment due date.
 *   - paid    → `paidFrom`/`paidTo`        date range over the recorded paid date.
 *
 * Sorts map to the backend's allow-listed `sort` fields ({dueDate, paidAt,
 * netAmountPaise}). The default (first entry) is "Due date (newest)" =
 * dueDate:desc — the grid's pre-existing visual order.
 *
 * Sortable column headers: Due (dueDate) and Paid (paidAt). BOTH directions are
 * registered for each so a header click can toggle desc↔asc without snapping
 * back to the default sort (an unregistered `field:dir` falls back to
 * `defaultSort`). The terse backend sort list only names `paidAt` desc, but the
 * explicit "sortable Paid header" requirement needs `paidAt:asc` too — the
 * backend contract allows `sort` over `paidAt` in either direction, so this is a
 * safe, deliberate registration. amount (netAmountPaise) stays dropdown-only.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const feesPaymentsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by name or admission number',
    },
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "All" chip. The backend reads an omitted
      // status as "all", so the default value is empty AND "All" is encoded by
      // omitting the param (no explicit `status=all`).
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'waived', label: 'Waived' },
        { value: 'partial', label: 'Partial' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'method',
      label: 'Paid via',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank transfer' },
        { value: 'upi_offline', label: 'UPI (offline)' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'other', label: 'Other' },
        { value: 'razorpay', label: 'Razorpay' },
      ],
    },
    {
      kind: 'date-range',
      key: 'due',
      label: 'Due',
      group: 'filters',
      params: { from: 'dueFrom', to: 'dueTo' },
    },
    {
      kind: 'date-range',
      key: 'paid',
      label: 'Paid',
      group: 'filters',
      params: { from: 'paidFrom', to: 'paidTo' },
    },
  ],
  sorts: [
    { value: 'dueDate:desc', label: 'Due date (newest)' },
    // Registered so the Due column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'dueDate:asc', label: 'Due date (oldest)' },
    { value: 'paidAt:desc', label: 'Paid date (newest)' },
    // Registered so the Paid column header can toggle to ascending (the terse
    // backend sort list only names paidAt:desc; the sortable Paid header needs
    // both directions and the contract allows sort over paidAt).
    { value: 'paidAt:asc', label: 'Paid date (oldest)' },
    { value: 'netAmountPaise:desc', label: 'Amount (high → low)' },
  ],
  resultNoun: ['payment', 'payments'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Due and Paid
 * both have their two directions registered above; amount stays dropdown-only.
 */
export const FEES_PAYMENTS_SORT_COLUMNS: Record<'dueDate' | 'paidAt', SortColumnBinding> = {
  dueDate: { field: 'dueDate', firstClickDir: 'desc' },
  paidAt: { field: 'paidAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Fees payments grid, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/finance/fees` opens on the configured defaults).
 *
 * The selected cycle is NOT a toolkit facet, so it is not declared here — it is
 * local component state (the cycle picker), not a URL-synced filter facet.
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `due` / `paid` are the date-range facets' PRESET keys
 * (e.g. `last7`), distinct from the concrete custom bounds `dueFrom`/`dueTo` and
 * `paidFrom`/`paidTo`; omitting them would stop a preset choice from sticking.
 * Kept beside the config (not in the route) so this boundary is unit-testable
 * without loading the route's React/router side effects.
 */
export const feesPaymentsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  method: z.string().optional(),
  due: z.string().optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
  paid: z.string().optional(),
  paidFrom: z.string().optional(),
  paidTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
